import { NextRouter } from 'next/router';

/**
 * Navigates back to the previous page with a fallback route
 *
 * This utility handles the common pattern of navigating back with a fallback,
 * addressing the issue that router.back is always truthy (it's a function).
 *
 * @param router - Next.js router instance
 * @param fallbackRoute - Route to navigate to if no browser history
 *
 * @example
 * // Basic usage with fallback
 * navigateBack(router, AdminRoutes.Dashboard);
 *
 * @example
 * // Multiple fallbacks
 * navigateBack(router, homeRoute || AdminRoutes.Dashboard);
 */
export const navigateBack = (router: NextRouter, fallbackRoute: string): void => {
  // Check if there's a previous page in history
  // Note: This is not perfect but is the best we can do
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back();
  } else {
    router.push(fallbackRoute);
  }
};

/**
 * Creates a navigation handler function for use in event handlers
 *
 * @param router - Next.js router instance
 * @param fallbackRoute - Route to navigate to if no history is available
 * @returns A function that can be used directly as an event handler
 *
 * @example
 * <Button onClick={createNavigateBackHandler(router, AdminRoutes.Dashboard)}>
 *   Back
 * </Button>
 */
export const createNavigateBackHandler = (router: NextRouter, fallbackRoute: string): (() => void) => {
  return () => navigateBack(router, fallbackRoute);
};
